import { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import type { PublicPortfolioImage } from '../api/portfolios';

// PortfolioLightbox — full-screen overlay för portfolio-bilder.
//
// Layout:
//   - Bilden fyller viewport-höjd (eller bredd om mycket bred), centrerad.
//   - Pilar för stega genom alla bilder i listan.
//   - Klick på bilden → toggla mellan "fit-to-viewport" och "1:1 zoom".
//   - ESC → stäng. Vänster/höger pilar → stega.
//   - Subtila kontroller: stäng-knapp, pilar, zoom-toggle, position-indicator.

interface Props {
  images: PublicPortfolioImage[];
  startIndex: number;
  onClose: () => void;
  onImageView?: (img: PublicPortfolioImage) => void;
}

export function PortfolioLightbox({ images, startIndex, onClose, onImageView }: Props) {
  const [index, setIndex] = useState(startIndex);
  const [zoomed, setZoomed] = useState(false);
  // Pan-offset när zoomed (för drag-to-pan)
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const current = images[index];

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : images.length - 1));
    setZoomed(false);
    setPan({ x: 0, y: 0 });
  }, [images.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i < images.length - 1 ? i + 1 : 0));
    setZoomed(false);
    setPan({ x: 0, y: 0 });
  }, [images.length]);

  // Tangentbord
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowLeft' || e.key === 'h') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' || e.key === 'l') {
        e.preventDefault();
        goNext();
      } else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setZoomed((z) => !z);
        setPan({ x: 0, y: 0 });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  // Tracka image-view när bilden ändras
  useEffect(() => {
    if (current && onImageView) onImageView(current);
  }, [current, onImageView]);

  // Lås body-scroll medan lightbox är öppen
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  function handleImageClick(e: React.MouseEvent) {
    // Pan-mode: om dragging precis hänt, hoppa över toggle
    e.stopPropagation();
    if (dragging) return;
    setZoomed((z) => !z);
    setPan({ x: 0, y: 0 });
  }

  function handleMouseDown(e: React.MouseEvent) {
    if (!zoomed) return;
    e.preventDefault();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setDragging(true);
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!dragging || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPan({
      x: dragStartRef.current.panX + dx,
      y: dragStartRef.current.panY + dy,
    });
  }

  function handleMouseUp() {
    setDragging(false);
    dragStartRef.current = null;
  }

  if (!current) return null;

  return (
    <div
      className="pv-lightbox"
      onClick={onClose}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Image */}
      <div
        className={`pv-lightbox-stage ${zoomed ? 'pv-lightbox-stage-zoomed' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          ref={imgRef}
          src={zoomed ? current.fullUrl : current.previewUrl}
          alt={current.filename}
          className="pv-lightbox-img"
          style={
            zoomed
              ? {
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(1)`,
                  cursor: dragging ? 'grabbing' : 'grab',
                  maxWidth: 'none',
                  maxHeight: 'none',
                  width: 'auto',
                  height: 'auto',
                }
              : undefined
          }
          onClick={handleImageClick}
          onMouseDown={handleMouseDown}
          draggable={false}
        />
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="pv-lightbox-close"
        aria-label="Close"
      >
        <X />
      </button>

      {/* Prev/Next */}
      {images.length > 1 ? (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="pv-lightbox-arrow pv-lightbox-arrow-prev"
            aria-label="Previous image"
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="pv-lightbox-arrow pv-lightbox-arrow-next"
            aria-label="Next image"
          >
            <ChevronRight />
          </button>
        </>
      ) : null}

      {/* Footer: position + zoom toggle */}
      <div className="pv-lightbox-footer" onClick={(e) => e.stopPropagation()}>
        <div className="pv-lightbox-counter">
          {index + 1} / {images.length}
        </div>
        <button
          type="button"
          onClick={() => {
            setZoomed((z) => !z);
            setPan({ x: 0, y: 0 });
          }}
          className="pv-lightbox-zoom"
          aria-label={zoomed ? 'Zoom out' : 'Zoom in'}
        >
          {zoomed ? <ZoomOut /> : <ZoomIn />}
        </button>
      </div>
    </div>
  );
}
